import Docker from 'dockerode';
import _ from 'lodash';

import environment from '@bmd-studio/genstack-environment';
import logger from '@bmd-studio/genstack-logger';
import { DockerEnv, MatchedContainer } from './index';
import { getCurrentServices } from './services';

const {
  APP_PREFIX,
  DOCKER_BACKEND_NETWORK_NAME,
  DOCKER_FRONTEND_NETWORK_NAME,
} = environment.env;

/**
 * Create a docker instance based on the current environment variables to connect to the deamon.
 */
export const getDockerInstance = (): Docker => {
  const {
    DOCKER_HOST_PROTOCOL,
    DOCKER_HOST_NAME,
    DOCKER_HOST_PORT,
    DOCKER_CERTIFICATE_DIR_PATH,
  } = environment.env;
  const dockerOptions: Docker.DockerOptions = {};

  // check if a custom host name is set when the container needs to be
  // deployed to another machine
  if (!_.isEmpty(DOCKER_HOST_NAME)) {
    dockerOptions.host = DOCKER_HOST_NAME;
    dockerOptions.port = DOCKER_HOST_PORT;
    dockerOptions.protocol = DOCKER_HOST_PROTOCOL;

    // check if SSL is enabled
    if (!_.isEmpty(DOCKER_CERTIFICATE_DIR_PATH)) {
      // TODO: implement
    }
  }

  logger.verbose.docker(`Attempting to connect to Docker daemon using options:`);
  logger.verbose.docker(dockerOptions);

  return new Docker(dockerOptions);
};

/**
 * Create the back-end network that most services will join. Other networks are specified in the services themselves.
 */
export const ensureBackendNetwork = async (): Promise<void> => {
  const networkName = `${APP_PREFIX}_${DOCKER_BACKEND_NETWORK_NAME}`;
  await ensureNetwork(networkName);
};

/**
 * Create the shared system-wide front-end network used for proxies.
 */
export const ensureFrontendNetwork = async (): Promise<void> => {
  const networkName = `${DOCKER_FRONTEND_NETWORK_NAME}`;
  await ensureNetwork(networkName);
};

/**
 * Ensure a certain network exists
 */
export const ensureNetwork = async (networkName: string): Promise<void> => {
  const dockerInstance = await getDockerInstance();
  const dockerEnv = getDockerEnv();
  const options: Docker.NetworkCreateOptions = {
    Name: networkName,
    CheckDuplicate: true,
    // @ts-ignore
    env: {
      ...dockerEnv,
    },
  };

  logger.info.services(`Creating the network with name ${networkName}...`);
  logger.verbose.services(`Network creation options:`);
  logger.verbose.services(options);
  
  try {
    await dockerInstance.createNetwork(options);
  } catch (error) {
    const { statusCode } = error;

    switch (statusCode) {
      case 200:
        logger.success.services(`Successfully created network ${networkName}.`);
        break;
      case 409:
        logger.success.services(`The network ${networkName} already exists.`);
        break;
      default:
        throw error;
    }
  }
};

/**
 * Get all the running docker containers that are applicable for this project.
 */
export const getActiveContainers = async (): Promise<Docker.ContainerInspectInfo[]> => {
  const dockerInstance = getDockerInstance();
  const containers = await dockerInstance.listContainers({ all: true });
  const appPrefixEnv = `APP_PREFIX=${APP_PREFIX}`;
  const activeContainers: Docker.ContainerInspectInfo[] = [];

  await Promise.all(_.map(containers, async (containerInfo) => {
    const containerInspectInfo = await dockerInstance.getContainer(containerInfo.Id).inspect();
    const { State } = containerInfo;

    // guard: check if the container is running
    if (State !== 'running') {
      return;
    }

    // guard: check if this container is started with the app prefix env variable
    if (!_.includes(containerInspectInfo.Config.Env, appPrefixEnv)) {
      return;
    }

    activeContainers.push(containerInspectInfo);
  }));

  return activeContainers;
};

/**
 * Get a service and container object that were matched through their name.
 */
export const getMatchedContainers = async (): Promise<MatchedContainer[]> => {
  const services = getCurrentServices();
  const containers = await getActiveContainers();

  const matchedContainers= _.map(containers, (container) => {
    const matchedService = _.find(services, (service) => {
      const serviceNameEnv = `SERVICE_NAME=${service.name}`;
      return _.includes(container.Config.Env, serviceNameEnv);
    });

    const matchedContainer: MatchedContainer = {
      container: container,
      service: matchedService,
    };

    return matchedContainer;
  });

  const fullMatchedContainers = _.filter<MatchedContainer>(matchedContainers, (matchedContainer) => {
    return !!matchedContainer.service;
  });

  return fullMatchedContainers;
};

/**
 * Construct the environment variables that can be passed to 
 * the Docker deamon when deploying the application using the Docker adapter.
 */
export const getDockerEnv = (): DockerEnv => {
  const {
    DOCKER_HOST_PROTOCOL,
    DOCKER_HOST_NAME,
    DOCKER_HOST_PORT = 2376,
    DOCKER_CERTIFICATE_DIR_PATH,
  } = environment.env;
  const composeName = getComposeName();

  // construct the environment variables that are passed to docker compose
  const dockerEnv: DockerEnv = {
    COMPOSE_PROJECT_NAME: composeName,
  };

  // check if a custom host name is set when the container needs to be
  // deployed to another machine
  if (!_.isEmpty(DOCKER_HOST_NAME)) {
    dockerEnv.DOCKER_HOST = `${DOCKER_HOST_PROTOCOL}://${DOCKER_HOST_NAME}:${DOCKER_HOST_PORT}`;

    // check if SSL is enabled
    if (!_.isEmpty(DOCKER_CERTIFICATE_DIR_PATH)) {
      dockerEnv.DOCKER_CERT_PATH = DOCKER_CERTIFICATE_DIR_PATH;
    }
  }

  return dockerEnv;
};

/**
 * Get the docker compose name of this project used to identify the project stack and containers
 */
export const getComposeName = (): string => {
  const {
    APP_PREFIX,
  } = environment.env;
  const composeName = `${APP_PREFIX}`;

  return composeName;
};